import React, { useMemo, useState } from 'react';
import {
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useAppTheme } from '../../utils/useAppTheme';
import numberMeanings from '../../data/numberMeanings.json';

const getMeaning = number => numberMeanings[String(number)] || null;

export default function WebNumberMeaning({ number }) {
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [visible, setVisible] = useState(false);
  const meaning = getMeaning(number);

  if (!meaning) return null;

  const open = event => {
    event?.stopPropagation?.();
    setVisible(true);
  };
  const close = event => {
    event?.stopPropagation?.();
    setVisible(false);
  };

  return (
    <>
      <TouchableOpacity
        style={styles.button}
        onPress={open}
        accessibilityRole="button"
        accessibilityLabel={`Biblical meaning of number ${number}`}
      >
        <Text style={styles.buttonText}>?</Text>
      </TouchableOpacity>
      <Modal visible={visible} transparent animationType="fade" onRequestClose={close}>
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={close}>
          <View style={styles.dialog}>
            <TouchableOpacity activeOpacity={1} onPress={event => event?.stopPropagation?.()}>
              <View style={styles.header}>
                <View style={styles.headerCopy}>
                  <Text style={styles.number}>{number}</Text>
                  <Text style={styles.title}>{meaning.t || 'No Established Biblical Symbolism'}</Text>
                </View>
                <TouchableOpacity style={styles.close} onPress={close}>
                  <Text style={styles.closeText}>×</Text>
                </TouchableOpacity>
              </View>
              <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
                {meaning.c && (
                  <View style={styles.confidence}>
                    <Text style={styles.confidenceText}>{meaning.c} Confidence</Text>
                  </View>
                )}
                {meaning.s ? (
                  <Text style={styles.summary}>{meaning.s}</Text>
                ) : (
                  <Text style={styles.empty}>No established traditional biblical symbolism is documented for this number.</Text>
                )}
                {meaning.m && (
                  <MeaningBlock styles={styles} label="Traditional Meaning" text={meaning.m} />
                )}
                {!meaning.m && meaning.st === 'multiple-or-competing-attested-associations' && meaning.r && (
                  <MeaningBlock styles={styles} label="Traditional Associations" text={meaning.r} />
                )}
                {!meaning.m && meaning.st === 'context-only-no-distilled-traditional-meaning' && meaning.r && (
                  <MeaningBlock styles={styles} label="Biblical Context" text={meaning.r} />
                )}
              </ScrollView>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
}

const MeaningBlock = ({ styles, label, text }) => (
  <View style={styles.meaningBlock}>
    <Text style={styles.meaningLabel}>{label}</Text>
    <Text style={styles.meaningText}>{text}</Text>
  </View>
);


const createStyles = colors => StyleSheet.create({
  button: {
    width: 20, height: 20, borderRadius: 10, borderWidth: 1.5,
    borderColor: colors.primary, alignItems: 'center', justifyContent: 'center',
    marginLeft: 6, cursor: 'pointer', flexShrink: 0,
  },
  buttonText: { color: colors.primary, fontSize: 12, fontWeight: '800', lineHeight: 15 },
  backdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center',
    justifyContent: 'center', padding: 16,
  },
  dialog: {
    width: '100%', maxWidth: 520, maxHeight: '85vh', backgroundColor: colors.surface,
    borderRadius: 14, overflow: 'hidden', borderWidth: 1, borderColor: colors.border,
  },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    padding: 18, backgroundColor: colors.primarySurface, borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerCopy: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12 },
  number: { color: colors.primary, fontSize: 28, fontWeight: '800' },
  title: { color: colors.text, fontSize: 17, fontWeight: '700', flex: 1 },
  close: { paddingHorizontal: 8, paddingVertical: 2, cursor: 'pointer' },
  closeText: { color: colors.textSecondary, fontSize: 28, lineHeight: 28 },
  body: { maxHeight: '65vh' },
  bodyContent: { padding: 18 },
  confidence: {
    alignSelf: 'flex-start', backgroundColor: colors.primarySurface,
    borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4, marginBottom: 12,
  },
  confidenceText: { color: colors.primary, fontSize: 12, fontWeight: '700' },
  summary: { color: colors.text, fontSize: 15, lineHeight: 24 },
  empty: { color: colors.textSecondary, fontSize: 14, lineHeight: 22, fontStyle: 'italic' },
  meaningBlock: {
    marginTop: 16, backgroundColor: colors.mutedSurface, borderRadius: 10, padding: 14,
  },
  meaningLabel: {
    color: colors.textSecondary, fontSize: 11, fontWeight: '700',
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6,
  },
  meaningText: { color: colors.text, fontSize: 14, lineHeight: 22 },
});